import clsx from 'clsx'
import { ChevronRight } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import type { QuickActionItem } from '@/types/dashboards'
import { useDrillDown } from './useDrillDown'

interface QuickActionsProps {
  actions: QuickActionItem[]
  className?: string
}

/** Right-rail shortcuts. A drill descriptor wins over a route, so an action
 *  that has a record set behind it opens the records rather than a page. */
export default function QuickActions({ actions, className }: QuickActionsProps) {
  const navigate = useNavigate()
  const { open } = useDrillDown()

  if (actions.length === 0) {
    return (
      <p className={clsx('text-[11px] text-stone-500', className)}>
        No actions are queued for you on this board.
      </p>
    )
  }

  return (
    <div className={clsx('space-y-1', className)}>
      {actions.map((action) => (
        <button
          key={action.id}
          type="button"
          onClick={() => {
            if (action.drill) open(action.drill)
            else if (action.to) navigate(action.to)
          }}
          disabled={!action.drill && !action.to}
          className="surface-panel flex w-full items-center gap-2 rounded-md px-2.5 py-1.5 text-left transition-colors hover:border-amber-400/30 hover:bg-amber-400/[0.04] disabled:cursor-default disabled:opacity-50 disabled:hover:border-amber-400/10 disabled:hover:bg-transparent"
        >
          <div className="min-w-0 flex-1">
            <div className="truncate text-[11px] font-medium text-stone-200">{action.label}</div>
            {action.description && (
              <div className="truncate text-[10px] text-stone-500">{action.description}</div>
            )}
          </div>
          {action.count != null && (
            <span className="flex-shrink-0 rounded border border-amber-400/20 bg-amber-400/[0.06] px-1.5 py-0.5 font-mono text-[9px] tabular-nums text-amber-300">
              {action.count.toLocaleString('en-US')}
            </span>
          )}
          <ChevronRight size={11} className="flex-shrink-0 text-stone-600" />
        </button>
      ))}
    </div>
  )
}
